import React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import useAnnouncementService from '../../api/services/announcementService';

const validationSchema = yup.object({
  messageTitle: yup.string().trim().required('Message title is required'),
  messageDescription: yup.string().trim(),
  messageBody: yup.string().trim().required('SMS message body is required'),
});

const EditSmsDialog = ({ open, onClose, announcement, onSuccess }) => {
  const { updateAnnouncement, isLoading } = useAnnouncementService();

  const initialValues = {
    messageTitle: announcement?.messageTitle || '',
    messageDescription: announcement?.messageDescription || '',
    messageBody: announcement?.messageBody || '',
  };

  const handleSubmit = async (values) => {
    const payload = {
      id: announcement.id,
      messageTitle: values.messageTitle.trim(),
      messageDescription: values.messageDescription.trim(),
      messageBody: values.messageBody.trim(),
      classId: announcement.classId,
      status: announcement.status || 'ACTIVE',
    };

    await updateAnnouncement(payload, () => {
      onSuccess?.();
      onClose();
    });
  };

  if (!announcement) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditOutlined color="primary" />
          <Typography variant="h6">Edit SMS Announcement</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form id="edit-sms-form">
              <Grid container spacing={2} mt={0.5}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Message Title"
                    name="messageTitle"
                    variant="outlined"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.messageTitle}
                    error={touched.messageTitle && !!errors.messageTitle}
                    helperText={touched.messageTitle && errors.messageTitle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Short Description (display only)"
                    name="messageDescription"
                    variant="outlined"
                    multiline
                    rows={2}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.messageDescription}
                    error={touched.messageDescription && !!errors.messageDescription}
                    helperText={touched.messageDescription && errors.messageDescription || 'Not sent as SMS — for card display only'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="SMS Message Body (sent to parents)"
                    name="messageBody"
                    variant="outlined"
                    multiline
                    rows={6}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.messageBody}
                    error={touched.messageBody && !!errors.messageBody}
                    helperText={touched.messageBody && errors.messageBody || 'This exact text is sent to parent phone numbers'}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="secondary" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-sms-form"
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSmsDialog;
